import React, { useEffect, useId, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { addCircleMember, lookupRepCode } from '../../store/actions/circleActions';
import { isRepCode, normalizeRepCode } from '../ForReps/v2/repUtils';
import { Avatar, BmModal, Btn, Card, Checkbox, Field, Input, SegmentedControl, Select } from '../ForBrands/ui/BrandUI';
import { usePhoneTheme } from '../ForBrands/useIsPhone';
import radioChecked from '../../assets/brand/onboarding/radio-checked-20.svg';
import iconMail from '../../assets/brand/icon-mail.svg';
import '../ForBrands/brandMode.css';

const RELATIONSHIPS = ['Parent', 'Guardian', 'Coach', 'Program Director', 'Trainer', 'Rep', 'Other'];

const PERMISSION_OPTIONS = [
  { value: 'view', label: 'View only', description: 'They can view your profile.' },
  { value: 'manage', label: 'Manage', description: 'They can edit your profile, but not add people to your Circle.' },
  { value: 'edit', label: 'Edit', description: 'They can edit your profile and add people to your Circle.' },
];

const MODES = [
  { value: 'email', label: 'Email' },
  { value: 'rep', label: 'Rep ID' },
];

const ARROW_STEP = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

// Pulls the JSON error body out of secureFetch's "HTTP NNN: {body}" message.
const parseFetchError = (err) => {
  const m = /HTTP \d+: (.+)$/s.exec(err?.message || '');
  if (!m) return null;
  try { return JSON.parse(m[1]); } catch { return { error: m[1] }; }
};

// The V2 option card (as OptionCard in the athlete onboarding pieces) as a
// real radio: role, aria-checked and one tab stop for the group.
const PermissionOption = ({ option, selected, buttonRef, onSelect, onKeyDown }) => (
  <button
    ref={buttonRef}
    type="button"
    role="radio"
    aria-checked={selected}
    tabIndex={selected ? 0 : -1}
    onClick={onSelect}
    onKeyDown={onKeyDown}
    className={`flex w-full items-start gap-[14px] rounded-[12px] bg-white p-4 text-left transition-colors ${
      selected ? 'border-2 border-bm-orange' : 'border border-bm-border hover:border-bm-strong'
    }`}
  >
    {selected
      ? <img src={radioChecked} alt="" className="h-5 w-5 shrink-0" />
      : <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-bm-strong bg-white" />}
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="bm-h-s text-bm-ink">{option.label}</span>
      <span className="bm-body-m text-bm-muted">{option.description}</span>
    </span>
  </button>
);

// The email field on phones (Input · Mobile with an icon, 2022:1738): the
// same icon and input as the desktop prefixed Input, in the 56px box with
// 16px text. .bm-phone-form sizes the plain fields but not a prefixed one.
const PhoneEmailInput = (props) => (
  <span className="flex h-14 items-center gap-[10px] rounded-[12px] border border-bm-border bg-white px-[18px] focus-within:border-bm-ink focus-within:shadow-[inset_0_0_0_1px_#2c2c2c]">
    <img src={iconMail} alt="" className="h-4 w-4 shrink-0 opacity-70" />
    <input
      className="bm-bare min-w-0 flex-1 bg-transparent font-inter text-[16px] leading-[22px] text-bm-ink outline-none placeholder:text-bm-placeholder"
      {...props}
    />
  </span>
);

// Athlete's "Add to your Circle": invite someone by email (name,
// relationship, permission) or link a rep by their Rep ID. Built on the
// V2 BmModal and BrandUI fields. In the phone theme (Athlete Mode below
// 640px) BmModal opens it as a bottom sheet and the form takes the 56px
// phone fields (.bm-phone-form); CircleManager in the Player Index has no
// phone theme and keeps the desktop form.
const AddCircleMemberModal = ({ open, onClose, onAdded, athleteEmail = null, initialMode = 'email' }) => {
  const dispatch = useDispatch();
  const formId = useId();
  const permissionLabelId = useId();
  const optionRefs = useRef([]);
  const phone = usePhoneTheme();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('Parent');
  const [permission, setPermission] = useState('view');
  const [repCode, setRepCode] = useState('');
  const [rep, setRep] = useState(null);          // resolved {email, name, agency}
  const [looking, setLooking] = useState(false);
  const [shareFinancials, setShareFinancials] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Resolve the Rep ID as soon as it looks complete so the athlete sees who
  // they are about to link.
  useEffect(() => {
    if (!open || mode !== 'rep') return undefined;
    const code = normalizeRepCode(repCode);
    if (!isRepCode(code)) {
      setRep(null);
      return undefined;
    }
    let cancelled = false;
    setLooking(true);
    dispatch(lookupRepCode(code))
      .then((found) => !cancelled && setRep(found))
      .catch(() => !cancelled && setRep({ missing: true }))
      .finally(() => !cancelled && setLooking(false));
    return () => { cancelled = true; };
  }, [repCode, mode, open, dispatch]);

  if (!open) return null;

  const reset = () => {
    setMode(initialMode);
    setEmail('');
    setName('');
    setRelationship('Parent');
    setPermission('view');
    setRepCode('');
    setRep(null);
    setShareFinancials(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let member;
      if (mode === 'rep') {
        const code = normalizeRepCode(repCode);
        if (!isRepCode(code)) return;
        member = await dispatch(addCircleMember({ repCode: code, permission: 'view', shareFinancials, athleteEmail }));
        toast.success(`${member?.name || 'Your rep'} is now linked to your Circle.`);
      } else {
        const trimmed = email.trim().toLowerCase();
        if (!trimmed) return;
        member = await dispatch(addCircleMember({
          email: trimmed,
          name: name.trim(),
          relationship,
          permission,
          athleteEmail,
          shareFinancials: relationship === 'Rep' ? shareFinancials : false,
        }));
        toast.success(`Added to your Circle. We emailed ${trimmed}.`);
      }
      if (onAdded) onAdded(member);
      reset();
      onClose();
    } catch (err) {
      const parsed = parseFetchError(err);
      toast.error(parsed?.error || 'Could not add to your Circle.');
    } finally {
      setSubmitting(false);
    }
  };

  // Arrow keys move the pick, as in a native radio group.
  const onPermissionKey = (e, index) => {
    const step = ARROW_STEP[e.key];
    if (!step) return;
    e.preventDefault();
    const next = (index + step + PERMISSION_OPTIONS.length) % PERMISSION_OPTIONS.length;
    setPermission(PERMISSION_OPTIONS[next].value);
    optionRefs.current[next]?.focus();
  };

  const canSubmit = mode === 'rep' ? isRepCode(repCode) && rep && !rep.missing && !looking : !!email.trim();
  const showShare = mode === 'rep' || relationship === 'Rep';
  const emailProps = {
    type: 'email',
    required: true,
    autoFocus: true,
    value: email,
    onChange: (e) => setEmail(e.target.value),
    placeholder: 'parent@example.com',
  };

  return (
    <BmModal
      title="Add to your Circle"
      onClose={handleClose}
      width={520}
      footer={
        <>
          <Btn variant="secondary" className="flex-1" onClick={handleClose} disabled={submitting}>
            Cancel
          </Btn>
          <Btn type="submit" form={formId} className="flex-1" disabled={submitting || !canSubmit}>
            {submitting ? 'Adding...' : mode === 'rep' ? 'Link rep' : 'Add to Circle'}
          </Btn>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className={phone ? 'bm-phone-form flex flex-col gap-4' : 'flex flex-col gap-4'}>
        {/* Each mode is a toggle button; aria-pressed marks the one in use.
            The labels are already sentence case ("Rep ID"), so keep them. */}
        <div role="group" aria-label="Add by">
          <SegmentedControl value={mode} onChange={setMode} options={MODES} capitalize={false} />
        </div>

        {mode === 'rep' ? (
          <>
            <p className="bm-body-m text-bm-muted">
              Your rep got a Rep ID when they signed up. Enter it here and we link
              their account, no email needed.
            </p>
            <Field label="Rep ID">
              <Input
                required
                autoFocus
                value={repCode}
                onChange={(e) => setRepCode(e.target.value.toUpperCase())}
                placeholder="REP-XXXXXX"
                maxLength={12}
                className="tracking-wider"
              />
            </Field>
            <div className="min-h-[18px]" aria-live="polite">
              {looking && <span className="bm-body-s text-bm-muted">Looking up...</span>}
              {!looking && rep && rep.missing && (
                <span className="bm-body-s text-bm-critical">No rep matches that Rep ID. Check it with them and try again.</span>
              )}
              {!looking && rep && !rep.missing && (
                <Card padding="px-4 py-3" className="flex items-center gap-3">
                  <Avatar name={rep.name} size={32} tone="outline" />
                  <span className="flex min-w-0 flex-col">
                    <span className="bm-h-xs text-bm-ink">
                      {rep.name}
                      {rep.agency ? ` · ${rep.agency}` : ''}
                    </span>
                    <span className="truncate bm-body-s text-bm-muted">{rep.email}</span>
                  </span>
                </Card>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="bm-body-m text-bm-muted">
              We'll email them an invite. If they don't have a CACHE account yet,
              they can create one with this email and their access will link up
              automatically.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Name (optional)">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
              </Field>
              <Field label="Relationship">
                <Select value={relationship} onChange={(e) => setRelationship(e.target.value)}>
                  {RELATIONSHIPS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Email">
              {phone ? (
                <PhoneEmailInput {...emailProps} />
              ) : (
                <Input {...emailProps} prefix={<img src={iconMail} alt="" className="mr-[6px] h-4 w-4 opacity-70" />} />
              )}
            </Field>
            <div className="flex flex-col gap-[6px]">
              <span id={permissionLabelId} className="bm-label-l text-bm-ink">Permission</span>
              <div role="radiogroup" aria-labelledby={permissionLabelId} className="flex flex-col gap-2">
                {PERMISSION_OPTIONS.map((opt, i) => (
                  <PermissionOption
                    key={opt.value}
                    buttonRef={(el) => { optionRefs.current[i] = el; }}
                    option={opt}
                    selected={permission === opt.value}
                    onSelect={() => setPermission(opt.value)}
                    onKeyDown={(e) => onPermissionKey(e, i)}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {showShare && (
          <Card padding="px-4 py-3" className="flex flex-col gap-1">
            <Checkbox checked={shareFinancials} onChange={setShareFinancials} label="Share financial terms" />
            <p className="pl-[30px] bm-body-s text-bm-muted">
              Reps always see deal status and timelines. Turn this on to also show offer amounts and payouts. You can change it later.
            </p>
          </Card>
        )}
      </form>
    </BmModal>
  );
};

export default AddCircleMemberModal;
